import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import ProductsDelegate from './products.delegate';
import { syncAdminProductCatalog } from '../common/lists';

class ProductsController extends Inherit(ShelfController, ProductsDelegate) {
    normalizeProductsResponse(response) {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.products)) return response.products;
        if (Array.isArray(response?.data?.products)) return response.data.products;
        return [];
    }

    async initialize() {
        if (!this.isAuthenticated) {
            this.navigateLogin();
            return;
        }

        await this.refreshProducts();
    }

    async refreshProducts({ changed = false } = {}) {
        // After a change here, the shared catalog (filters, pickers) follows right away.
        if (changed) syncAdminProductCatalog({ force: true }).catch(() => {});
        try {
            this.setState({ loading: true });
            const response = await this.data.fetchProducts(1, 5000, null, null, true);
            const products = this.normalizeProductsResponse(response);
            // APIs that cap the page size (e.g. at 200) answer with more pages: follow them.
            for (let page = 2; response?.pagination?.hasNext && page <= 100; page += 1) {
                const next = await this.data.fetchProducts(page, 5000, null, null, true);
                products.push(...this.normalizeProductsResponse(next));
                if (!next?.pagination?.hasNext) break;
            }
            this.setState({ products, rows: products, loading: false });
        } catch (error) {
            console.error('Error fetching products:', error);
            this.props.onMessage('error', 'No se pudieron cargar los productos');
            this.setState({ loading: false });
        }
    }

    /** Category options for the drawer, taken from the loaded catalog (unique, sorted). */
    get categoryOptions() {
        const { products } = this.state;
        const categories = [...new Set((products || []).map((product) => product?.category).filter(Boolean))].sort((a, b) => a.localeCompare(b));
        return categories.map((category) => ({ value: category, label: category }));
    }

    /** Filters the table by name, code or category (case-insensitive). */
    searchProducts(value) {
        const { products } = this.state;
        const term = (value || '').toLowerCase().trim();
        if (!term) {
            this.setRows(products);
            return;
        }

        const filtered = products.filter((product) => {
            return (
                product?.name?.toLowerCase().includes(term) ||
                product?.value?.toLowerCase().includes(term) ||
                product?.category?.toLowerCase().includes(term)
            );
        });
        this.setRows(filtered);
    }

    openCreateDrawer() {
        this.setState({ selectedProduct: null, isOpen: true });
    }

    async createOrUpdateProduct(payload) {
        try {
            const { selectedProduct } = this.state;
            if (selectedProduct?._id) {
                await this.data.updateProductById(selectedProduct._id, payload);
                this.props.onMessage('success', 'Producto actualizado con éxito');
            } else {
                await this.data.createProduct(payload);
                this.props.onMessage('success', 'Producto creado con éxito');
            }

            this.setState({ isOpen: false, selectedProduct: null });
            await this.refreshProducts({ changed: true });
        } catch (error) {
            console.error('Product save error:', error);
            const message = error?.response?.data?.error || error?.message || 'No se pudo guardar el producto';
            this.props.onMessage('error', message);
        }
    }

    async uploadProductImage(file) {
        const response = await this.data.uploadProductImage(file);
        return response?.image || null;
    }

    // Searchable auditor-barcode lookup for the product edit drawer.
    async searchBarcodes(query) {
        try {
            const response = await this.data.fetchAuditAdminAuditProducts({ search: query, limit: 20 });
            return response?.data?.products || response?.products || [];
        } catch (error) {
            console.error('Barcode search error:', error);
            return [];
        }
    }

    async toggleProductActive(product) {
        try {
            await this.data.updateProductById(product._id || product.value, { active: !product.active });
            this.props.onMessage('success', product.active ? 'Producto desactivado' : 'Producto reactivado');
            await this.refreshProducts({ changed: true });
        } catch (error) {
            console.error('Toggle product active error:', error);
            this.props.onMessage('error', 'No se pudo actualizar el estado del producto');
        }
    }

}

export default ProductsController;
